import React, { useState, useRef, useEffect } from 'react';
import useSupercluster from "use-supercluster";
import GoogleMapReact from "google-map-react";
import InfoWindow from '../InfoWindow';
import './Map.css';

// Estilos para tema escuro: remove POIs (parques, shoppings, etc.) e destaca divisões estaduais
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1f2a37" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#000000" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  // Esconde pontos de interesse e estações/transporte
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi.attraction", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "transit.station", stylers: [{ visibility: "off" }] },
  // Destaca fronteiras estaduais e nacionais
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [
    { color: "#ffcc66" }, { visibility: "on" }, { weight: 2 }
  ] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [
    { color: "#ffaa00" }, { visibility: "on" }, { weight: 2 }
  ] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [
    { color: "#4a5568" }
  ] },
  // Estradas e água com contraste
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#70b5ff" }] }
];

// Estilos para tema claro: remove POIs e destaca divisão dos estados
const lightMapStyles = [
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi.attraction", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "transit.station", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [
    { color: "#333333" }, { visibility: "on" }, { weight: 2 }
  ] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [
    { color: "#000000" }, { visibility: "on" }, { weight: 2 }
  ] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c0d6eb" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f5d480" }] }
];

// Componente de marcador que descarta props internos do google-map-react ($hover, $prerender, lat, lng)
const MapMarker = ({ $hover, $prerender, lat, lng, onClick, children, className }) => {
  return (
    <div onClick={onClick} className={className} style={{ position: 'relative' }}>
      {children}
    </div>
  );
};

const Map = (props) => {
  const mapRef = useRef();
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);

  const { points, mapPoints, darkMode, useUserLocation } = props;
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(4); // Brasil inteiro

  const [center, setCenter] = useState({
    lat: -14.2350, // Centro aproximado do Brasil
    lng: -51.9253
  });
  
  const [mapStyles, setMapStyles] = useState(darkMode ? darkMapStyles : lightMapStyles);

  // Atualiza os estilos do mapa quando o tema muda
  useEffect(() => {
    setMapStyles(darkMode ? darkMapStyles : lightMapStyles);
  }, [darkMode]);

  // Centraliza próximo à localização do usuário; se indisponível, mostra Brasil inteiro
  useEffect(() => {
    if (!useUserLocation) return;
    if (!navigator.geolocation) {
      // Fallback: Brasil inteiro
      setCenter({ lat: -14.2350, lng: -51.9253 });
      setZoom(4);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (
          typeof latitude === 'number' &&
          typeof longitude === 'number' &&
          !Number.isNaN(latitude) &&
          !Number.isNaN(longitude)
        ) {
          setCenter({ lat: latitude, lng: longitude });
          // Mantém visão mais distante ao abrir com geolocalização
          setZoom(5);
        }
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn('Geolocation error:', err && err.message ? err.message : err);
        // Fallback: Brasil inteiro
        setCenter({ lat: -14.2350, lng: -51.9253 });
        setZoom(4);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  }, [useUserLocation]);

  // Desenha uma rota no mapa quando directionsRequest é passado via props
  useEffect(() => {
    // Se o mapa ainda não está disponível, não faz nada
    if (!mapRef.current) return;

    // Se não há solicitação de direções, limpa a rota existente (se houver)
    if (!props.directionsRequest) {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
      }
      return;
    }

    const google = window.google;
    if (!google || !google.maps) return;

    // Cria (uma vez) os serviços de direções
    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new google.maps.DirectionsService();
    }

    // Cria (se necessário) o renderer e associa ao mapa atual
    if (!directionsRendererRef.current) {
      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        suppressMarkers: false,
        polylineOptions: {
          strokeColor: darkMode ? '#70b5ff' : '#1a73e8',
          strokeWeight: 5
        }
      });
      directionsRendererRef.current.setMap(mapRef.current);
    } else {
      // Atualiza o estilo da linha quando o tema muda
      directionsRendererRef.current.setOptions({
        polylineOptions: {
          strokeColor: darkMode ? '#70b5ff' : '#1a73e8',
          strokeWeight: 5
        }
      });
    }

    // Normaliza o travelMode caso venha como string
    const req = { ...props.directionsRequest };
    if (req.travelMode && typeof req.travelMode === 'string') {
      const mode = req.travelMode.toUpperCase();
      req.travelMode = (google.maps.TravelMode && google.maps.TravelMode[mode]) || google.maps.TravelMode.DRIVING;
    }

    directionsServiceRef.current.route(req, (res, status) => {
      if (status === 'OK') {
        directionsRendererRef.current && directionsRendererRef.current.setDirections(res);
      } else {
        // eslint-disable-next-line no-console
        console.warn('Directions request failed:', status);
      }
    });

    // Não removemos o renderer aqui para manter a rota enquanto o componente existir;
    // a limpeza acontece quando directionsRequest é removido ou no unmount.
  }, [props.directionsRequest, darkMode]);

  const handleCloseInfoWindown = () => {
    setSelectedEquipment(null);
    setCenter({
      lat: -14.2350,
      lng: -51.9253
    });
    setZoom(4);
  }

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 30, maxZoom: 15 }
  });

  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const mapOptions = (isDark) => ({
    styles: isDark ? darkMapStyles : lightMapStyles,
    fullscreenControl: false,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    rotateControl: false,
    clickableIcons: false,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    mapTypeControlOptions: {
      style: 2, // DROPDOWN_MENU
      position: 3 // RIGHT_TOP
    }
  });

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
        defaultCenter={{
          lat: -14.2350,
          lng: -51.9253
        }}
        center={center}
        zoom={zoom}
        defaultZoom={4}
        options={mapOptions(darkMode)}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map;
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom);
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat
          ]);
        }}
      >
        {clusters.map(marker => {
          const [longitude, latitude] = marker.geometry.coordinates;
          const {
            cluster: isCluster,
            point_count: pointCount
          } = marker.properties;

          if (mapPoints === true) {
            return (
              <MapMarker
                key={marker.id || `marker-${latitude}-${longitude}`}
                lat={latitude}
                lng={longitude}
                onClick={() => setSelectedEquipment(marker)}
              >
                <img
                  alt=""
                  src={marker ? '/images/redMarker.png' : ''}
                  className="marker-image"
                  onClick={() => {
                    setSelectedEquipment(marker.properties.data)
                  }}
                />
              </MapMarker>
            )
          }

          if (isCluster) {
            return (
              <MapMarker
                key={marker.id || `marker-${latitude}-${longitude}`}
                lat={latitude}
                lng={longitude}
              >
                <div
                  className="cluster map-cluster"
                  style={{
                    width: `${15 + (pointCount / points.length) * 30}px`,
                    height: `${15 + (pointCount / points.length) * 30}px`,
                    zIndex: 9999
                  }}
                  onClick={() => {
                    const expansionZoom = Math.min(
                      supercluster.getClusterExpansionZoom(marker.id),
                      20
                    );
                    mapRef.current.setZoom(expansionZoom);
                    mapRef.current.panTo({ lat: latitude, lng: longitude });
                  }}
                >
                  {pointCount}
                </div>
              </MapMarker>
            )
          }

          return (
            <MapMarker
              key={marker.id || `marker-${latitude}-${longitude}`}
              lat={latitude}
              lng={longitude}
            >
              {marker?.properties?.data?.isConnected ?
                <div
                  className="badge-online map-badge-online"
                  style={{
                    position: 'absolute',
                    top: '-15px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'var(--map-online-badge-text)',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    zIndex: 998,
                    boxShadow: 'var(--map-marker-shadow)',
                    border: "solid 1px white"
                  }}
                >
                  {"Online"}
                </div>
                : null
              }

              {(marker?.properties?.data?.expiredCalibration || marker?.properties?.data?.nearCalibration) && (
                <div
                  className="badge-calibration map-badge-calibration"
                  style={{
                    position: 'absolute',
                    top: '-30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: marker?.properties?.data?.expiredCalibration ? '#fff' : '#222',
                    background: marker?.properties?.data?.expiredCalibration ? 'red' : 'orange',
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    zIndex: 999,
                    border: '1px solid #fff'
                  }}
                  title={marker?.properties?.data?.expiredCalibration ? 'Calibração expirada' : 'Calibração perto de 1 ano'}
                >
                  {marker?.properties?.data?.expiredCalibration ? 'Calibração expirada' : 'Calibração em breve'}
                </div>
              )}

              <span className="marker-label">{marker?.properties?.data?.serial}</span>
              <img
                alt="img"
                src={
                  marker.properties.data.serial.startsWith("CP")
                    ? '/images/cp.png'
                    : marker.properties.data.serial.startsWith("SI") || marker.properties.data.serial.startsWith("ES")
                      ? '/images/esi.png'
                      : marker.properties.data.serial.startsWith("BI") || marker.properties.data.serial.startsWith("FB")
                        ? '/images/fbi.png'
                        : marker.properties.data.serial.startsWith("EE")
                          ? '/images/ee.png'
                          : '/images/999dry.png'
                }
                className={`marker ${marker.located ? 'equip_marker_img_located' : 'equip_marker_img'}`}
                style={{
                  border: marker.properties.data.expiredCalibration
                    ? '3px solid red'
                    : marker.properties.data.nearCalibration90
                      ? '3px solid orange'
                      : marker.properties.data.nearCalibration
                        ? '3px solid gold'
                        : '2px solid transparent',
                  boxShadow: marker.properties.data.expiredCalibration || marker.properties.data.nearCalibration90 || marker.properties.data.nearCalibration
                    ? '0 0 10px rgba(255,165,0,0.6)'
                    : 'var(--map-marker-shadow)'
                }}
                onClick={() => {
                  setSelectedEquipment(marker.properties.data);
                }}
              />
            </MapMarker>
          )
        })}

        {selectedEquipment &&
          <InfoWindow
            close={handleCloseInfoWindown}
            data={selectedEquipment}
            lat={selectedEquipment.lat}
            lng={selectedEquipment.lng}
            setCenter={setCenter}
            darkMode={darkMode}
          />
        }
      </GoogleMapReact>
    </div>
  )
}

export default Map;
